import {BigNumber} from 'ethers';
import {formatUnits} from 'ethers/lib/utils';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {isIOS, isMobile} from 'react-device-detect';
import {useFormContext} from 'react-hook-form';
import * as Yup from 'yup';
import {formatBigNumber} from '../../lib/utilities';
import {useGetPictureUrlQuery, useGetTokensQuery} from '../../redux/projects.api';
import {selectWallet} from '../../redux/wallet.slice';
import {useAppSelector} from '../hooks';
import useFrame from './useFrame';
import useInvestFormValidation from './useInvestFormValidation';
import useInvestSubmit from './useInvestSubmit';
import useNetworkToken from './useNetworkToken';
import useProjectById from './useProjectById';
import useReferralCode, {REF_CODE_KEY} from './useReferralCode';

interface ISubmitProps {
  title: string;
  onClick?: VoidFunction;
  type?: 'submit' | 'reset';
  disabled?: boolean;
  component?: 'a';
  href?: string;
}

const useInvestForm = (projectId: string, isEmbed?: boolean) => {
  const {address} = useAppSelector(selectWallet);

  const {data: allTokens} = useGetTokensQuery();
  const {tokens} = useNetworkToken(allTokens);

  const {showConnect} = useFrame();
  const {
    register,
    handleSubmit,
    formState: {errors},
    reset,
    watch,
    setValue,
    control,
  } = useFormContext();
  const {showConfirm} = useFrame();

  const [inited, setInited] = useState(false);
  const projectData = useProjectById(projectId);
  const amount = watch('amount');
  const {referralCode} = useReferralCode();
  const {onSubmit} = useInvestSubmit(projectId);

  const {data: pictureUrl} = useGetPictureUrlQuery(projectData.project?.tokenIcon!, {
    skip: !projectData.project?.tokenIcon,
  });

  const {formError} = useInvestFormValidation(projectData, isEmbed);

  useEffect(() => {
    if (projectData.project && projectData.project.defaultAmount && !+amount && !inited) {
      const formatted = formatBigNumber(BigNumber.from(projectData.project.defaultAmount));
      setValue('amount', formatted);
      setInited(true);
    }
  }, [amount, inited, projectData, setValue]);

  const selectedToken = useMemo(() => {
    if (!projectData.tokens) return undefined;
    const tokenAddress = watch('token');
    const token = projectData.tokens.find(el => tokenAddress.toLowerCase() === el.address?.toLowerCase());
    return token;
  }, [projectData, watch]);

  const isUnsupportedConnect = useMemo(() => {
    return isIOS && isMobile && isEmbed;
  }, [isEmbed]);

  const formatterMinAmount = useMemo(() => formatUnits(projectData.project?.minAmount || '0'), [projectData]);
  const formatterMaxAmount = useMemo(() => formatUnits(projectData.project?.maxAmount || '0'), [projectData]);

  const fields = {
    amount: register('amount', {
      required: {
        value: true,
        message: 'Required',
      },
      min: {
        value: formatterMinAmount,
        message: `Must be greater then ${formatterMinAmount}`,
      },
      max: {
        value: formatterMaxAmount,
        message: `Must be less than ${formatterMaxAmount}`,
      },
      disabled: !!formError,
    }),
    token: {
      control,
      name: 'token',
      validation: {
        required: {
          value: true,
          message: 'Required',
        },
      },

      disabled: !!formError,
    },
    address: {
      control,
      name: 'address',
      validation: {
        required: {
          value: true,
          message: 'Required',
        },
        pattern: {
          value: /0x[a-fA-F0-9]{40}/g,
          message: 'Incorrect address',
        },
      },
      disabled: !!formError,
    },
    email: {
      control,
      name: 'email',
      validation: {
        required: {
          value: true,
          message: 'Required',
        },
        validate: (value: string) => {
          try {
            Yup.string().email('Must be a valid email').validateSync(value);
          } catch (err) {
            return (err as Error).message;
          }
          return undefined;
        },
      },
      disabled: !!formError,
    },
    fullName: {
      control,
      name: 'fullName',
      validation: {
        required: {
          value: true,
          message: 'Required',
        },
      },
      disabled: !!formError,
    },
    telegram: {
      control,
      name: 'telegram',
      disabled: !!formError,
    },
    story: {
      control,
      name: 'story',
      validation: {},
      disabled: !!formError,
    },
  };

  useEffect(() => {
    if (projectData.tokens && projectData.tokens[0] && projectData.tokens[0].address && !selectedToken) {
      setValue('token', projectData.tokens[0].address);
    }
  }, [projectData, selectedToken, setValue]);

  const setMaxAmount = useCallback(() => {
    if (!!formError) return;
    setValue('amount', String(+formatUnits(projectData.project?.maxAmount || '0')));
  }, [formError, projectData, setValue]);

  const setMinAmount = useCallback(() => {
    if (!!formError) return;
    setValue('amount', String(+formatUnits(projectData.project?.minAmount || '0')));
  }, [formError, projectData, setValue]);

  const tokenIcon = useMemo(() => {
    return projectData.project && projectData.project.tokenIcon ? pictureUrl?.fileUrl : undefined;
  }, [pictureUrl?.fileUrl, projectData.project]);

  // const [isTokenIconExist, setTokenIconExist] = useState<boolean>();

  // useEffect(() => {
  //   if (tokenIcon && typeof isTokenIconExist !== 'boolean') {
  //     setTokenIconExist(isImageExist(tokenIcon));
  //   }
  // }, [tokenIcon, isTokenIconExist]);

  const submitProps = useMemo<ISubmitProps>(() => {
    if (isUnsupportedConnect) {
      return {
        title: 'Go to investing',
        onClick: () => {
          window.open(`${process.env.REACT_APP_FORM_ROOT}/${projectId}/?${REF_CODE_KEY}=${referralCode}`);
        },
      };
    } else if (!address) {
      return {
        title: 'Connect Wallet',
        onClick: () => {
          showConnect();
        },
      };
    } else {
      return {
        type: 'submit',
        disabled: !!formError || !!Object.values(errors).some(el => el),
        title: 'Invest',
      };
    }
  }, [address, errors, formError, isUnsupportedConnect, projectId, referralCode, showConnect]);

  // if (!projectData) return null;
  return {
    onSubmit: onSubmit,
    fields,
    tokens: projectData.tokens,
    project: projectData.project,
    errors,
    watch,
    setMaxAmount,
    setMinAmount,
    investAmount: {
      minInvest: projectData.project?.minAmount || '0',
      maxInvest: projectData.project?.maxAmount || '0',
      token: selectedToken,
      rate: formatBigNumber(BigNumber.from(projectData.project?.exchageRate || '1')),
      tgeToken: projectData.project?.tgeToken,
      tokenIcon: tokenIcon,
      value: amount || formatBigNumber(BigNumber.from(projectData.project?.defaultAmount || '1')),
    },
    confirmModal: {
      open: handleSubmit(() => showConfirm()),
    },
    formState: {
      error: formError,
    },
    submitButton: submitProps,
  };
};

export default useInvestForm;
